"use client"

import { useEffect } from "react"
import { useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import {
  Card,
  Divider,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Badge,
  Avatar,
  SkeletonCircle,
  SkeletonText,
  Tooltip,
  Box,
  Flex,
  Container,
  Tag,
  TagLabel,
  Progress,
  HStack,
  VStack,
  Text,
  Grid,
  GridItem,
  Stack,
  Button,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from "@chakra-ui/react"
import {
  BriefcaseIcon,
  AcademicCapIcon,
  CodeBracketIcon,
  MapPinIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  CheckBadgeIcon,
  GlobeAltIcon,
  IdentificationIcon,
  CakeIcon,
  HeartIcon,
  LinkIcon,
  ArrowDownTrayIcon,
  Bars3Icon,
  UserCircleIcon,


} from "@heroicons/react/24/outline"
import {
  fetchCandidateDetailsAsync,
  selectCandidateDetails,
} from "../../../../employerDashboardSlice/ShowCandidateDetails/ShowCandidateDetailsSlice"
import { CANDIDATE_PROFILE, RESUME_VIEW } from "../../../../../common/URLs"

function ShowCandidate() {
  const dispatch = useDispatch()
  const params = useParams()
  const candidateId = params.uuid
  const { isOpen, onOpen, onClose } = useDisclosure()

  const { data: candidateData, loading, error } = useSelector(selectCandidateDetails)


  useEffect(() => {
    if (candidateId) {
      dispatch(fetchCandidateDetailsAsync(candidateId))
    }
  }, [dispatch, candidateId])

  // Format salary to display in lakhs/crores
  const formatSalary = (amount) => {
    if (!amount) return "N/A"

    if (amount >= 10000000) {
      return `${(amount / 10000000).toFixed(2)} Cr`
    } else if (amount >= 100000) {
      return `${(amount / 100000).toFixed(2)} Lakh`
    } else {
      return `${amount.toLocaleString()}`
    }
  }

  // Get all unique skills from different skill arrays
  const getAllSkills = () => {
    if (!candidateData) return []

    const skillSets = [
      ...(candidateData.skills || []).map((s) => s.skill?.title),
      ...(candidateData.it_skills || []).map((s) => s.skill?.title),
      ...(candidateData.job_preference || []).flatMap((jp) => jp.preference_skills?.map((ps) => ps.skill?.title) || []),
    ]

    return [...new Set(skillSets)].filter(Boolean)
  }
  const openPdf = (url) => {
    if (!url) {
      console.error("File URL is missing.")
      return
    }
    const newTab = window.open(url, "_blank")
    if (!newTab) {
      console.error("Failed to open file. Please check your browser settings.")
    }
  }
  // Calculate experience level for skill visualization
  const getExperienceLevel = (years) => {
    if (years >= 5) return { level: "Expert", percentage: 90, color: "blue" }
    if (years >= 3) return { level: "Advanced", percentage: 75, color: "cyan" }
    if (years >= 1) return { level: "Intermediate", percentage: 60, color: "teal" }
    return { level: "Beginner", percentage: 40, color: "gray" }
  }

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-mainBg  min-h-screen p-4 sm:p-6">
        <Container maxW="container.xl">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Section - Profile Skeleton */}
            <div className="w-full lg:w-1/3">
              <Card className="shadow-lg rounded-xl border border-gray-200 mb-6 overflow-hidden">
                <Box h="80px" bg="blue.600" />
                <Box p={5} mt="-40px">
                  <Flex direction="column" align="center">
                    <SkeletonCircle size="24" mb={4} />
                    <SkeletonText mt="2" noOfLines={3} spacing="3" width="full" />
                  </Flex>
                  <Divider my={4} />
                  <SkeletonText mt="2" noOfLines={5} spacing="3" />
                </Box>
              </Card>

              <Card className="shadow-lg rounded-xl border border-gray-200 overflow-hidden">
                <Box p={5}>
                  <SkeletonText mt="2" noOfLines={6} spacing="3" />
                </Box>
              </Card>
            </div>

            {/* Right Section - Skills, Projects, Education, Experience Skeleton */}
            <div className="w-full lg:w-2/3">
              <Card className="shadow-lg rounded-xl border border-gray-200 mb-6 overflow-hidden">
                <Box p={5}>
                  <SkeletonText mt="2" noOfLines={1} spacing="3" />
                  <Flex mt={4} wrap="wrap" gap={2}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <SkeletonText key={i} noOfLines={1} width="80px" />
                    ))}
                  </Flex>
                </Box>
              </Card>

              <Card className="shadow-lg rounded-xl border border-gray-200 overflow-hidden">
                <Box p={5}>
                  <SkeletonText mt="2" noOfLines={8} spacing="3" />
                </Box>
              </Card>
            </div>
          </div>
        </Container>
      </div>
    )
  }

  // Error state
  // Error state
  if (error) {
    return (
      <div className="min-h-screen  bg-mainBg flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-6 text-center shadow-xl rounded-xl border border-red-200">
          <div className="text-red-500 mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-14 w-14 mx-auto mb-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h3 className="text-xl font-bold">Error Loading Candidate Data</h3>
          </div>
          <div className="text-gray-600 mb-5">
            {typeof error === 'object' && error.message ? (
              <>
                <p className="font-medium">Error Details:</p>
                <p className="text-sm mt-1">{error.message}</p>
                {error.response && error.response.data && (
                  <p className="text-xs mt-2">Server response: {JSON.stringify(error.response.data)}</p>
                )}
              </>
            ) : (
              <p>{error}</p>
            )}
          </div>
          <button
            onClick={() => dispatch(fetchCandidateDetailsAsync(candidateId))}
            className="w-full py-2 px-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200"
          >
            Try Again
          </button>
        </Card>
      </div>
    )
  }

  // No data state
  if (!candidateData) {
    return (
      <div className="min-h-screen  bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-6 text-center shadow-xl rounded-xl border border-yellow-200">
          <div className="text-yellow-500 mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-14 w-14 mx-auto mb-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-xl font-bold">No Candidate Data Available</h3>
          </div>
          <p className="text-gray-600 mb-5">Could not find candidate information for the provided ID.</p>
          <button
            onClick={() => dispatch(fetchCandidateDetailsAsync(candidateId))}
            className="w-full py-2 px-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200"
          >
            Try Again
          </button>
        </Card>
      </div>
    )
  }

  // Mobile drawer for profile info
  const MobileProfileDrawer = () => (
    <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="full">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" bg="blue.600" color="white">
          Profile Information
        </DrawerHeader>
        <DrawerBody p={0}>
          {/* Profile Card */}
          <ProfileCard />

          {/* Job Preferences Card */}
          <JobPreferencesCard />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )

  // Profile Card Component
  const ProfileCard = () => (
    <Card className="shadow-xl rounded-xl border border-gray-200 overflow-hidden mb-6 transition-all duration-300 ">
      {/* Profile Header with Gradient */}
      <Box h="80px" bgGradient="linear(to-r, blue.600, blue.500, cyan.500)" position="relative" overflow="hidden">
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bgImage="url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNDQwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDE0NDAgMTAwIiBmaWxsPSJub25lIj48cGF0aCBkPSJNMCAwaDEyMDBjMTMzLjMzMyAwIDIwMCAzMy4zMzMzIDIwMCAxMDBIMFYwWiIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIi8+PC9zdmc+')"
          bgRepeat="no-repeat"
          bgPosition="bottom"
          bgSize="cover"
          opacity="0.3"
        />
      </Box>

      <Box p={5} position="relative">
        {/* Avatar with Status */}
        <Flex justify="center" mt="-40px" mb={4}>
          <Box position="relative">
            <Avatar
              size="xl"
              name={candidateData?.name || "User"}
              src={`${CANDIDATE_PROFILE}${candidateData?.basic_details?.profile_pic}` || ""}
              border="4px solid white"
              bg="blue.500"
              className="shadow-lg object-contain"
            />
            {candidateData?.profile_verified ? (
              <Box
                position="absolute"
                bottom="0"
                right="0"
                bg="green.400"
                borderRadius="full"
                p="1"
                border="2px solid white"
              >
                <CheckBadgeIcon className="h-4 w-4 text-white" />
              </Box>
            ) : (
              <Box
                position="absolute"
                bottom="0"
                right="0"
                bg="blue.100"
                borderRadius="full"
                p="1"
                border="2px solid white"
              >
                <Box w="3" h="3" bg="blue.500" borderRadius="full" />
              </Box>
            )}
          </Box>
        </Flex>

        {/* Name and Title */}
        <Box textAlign="center" mb={3}>
          <div fontSize="xl" fontWeight="bold" color="gray.800" className="tracking-tight text-gray-700 font-semibold text-xl" >
            {candidateData?.name || "N/A"}
          </div>
          <Text fontSize="sm" color="blue.600" fontWeight="medium">
            {candidateData?.current_company_details?.job_title?.title || "Professional"}
          </Text>

          <HStack justify="center" mt={1} spacing={2} color="gray.500" fontSize="sm">
            <Flex align="center">
              <BriefcaseIcon className="h-4 w-4 mr-1" />
              <div>{candidateData?.basic_details?.experience_year || 0} years</div>
            </Flex>
            <div>•</div>
            <Flex align="center">
              <MapPinIcon className="h-4 w-4 mr-1" />
              <div>{candidateData?.basic_details?.city?.name || "N/A"}</div>
            </Flex>
          </HStack>

          {/* Status Badge */}
          {candidateData?.basic_details?.active_job_search ? (
            <Badge mt={2} colorScheme="green" variant="subtle" px={2} py={1} borderRadius="full" className="shadow-sm">
              Actively Looking
            </Badge>
          ) : null}
        </Box>

        <Divider mb={4} />

        {/* Contact Information */}
        <VStack align="stretch" spacing={4}>
          <HStack spacing={3} align={"start"}>
            <Flex align="" justify="center" bg="blue.100" p={2.5} borderRadius="lg" className="shadow-sm">
              <PhoneIcon className="h-4 w-4 text-blue-600" />
            </Flex>
            <Box>
              <div fontSize="xs" color="gray.500" fontWeight="medium" className="text-xs text-gray-500 font-medium">
                Phone
              </div>
              <div fontSize="sm" fontWeight="medium" className="text-sm font-medium text-gray-700">
                {candidateData?.phone || "N/A"}
              </div>
            </Box>
          </HStack>

          <HStack spacing={3} align={"start"}>
            <Flex align="center" justify="center" bg="blue.100" p={2.5} borderRadius="lg" className="shadow-sm">
              <EnvelopeIcon className="h-4 w-4 text-blue-600" />
            </Flex>
            <Box>
              <div fontSize="xs" color="gray.500" fontWeight="medium" className="text-xs text-gray-500 font-medium" >
                Email
              </div>
              <Tooltip label={candidateData?.email} placement="top">
                <div fontSize="sm" fontWeight="medium" isTruncated maxW="220px" className="text-sm font-medium text-gray-700">
                  {candidateData?.email || "N/A"}
                </div>
              </Tooltip>
            </Box>
          </HStack>

          <HStack spacing={3} align={"start"}>
            <Flex align="center" justify="center" bg="blue.100" p={2.5} borderRadius="lg" className="shadow-sm">
              <MapPinIcon className="h-4 w-4 text-blue-600" />
            </Flex>
            <Box>
              <div fontSize="xs" color="gray.500" fontWeight="medium" className="text-xs text-gray-500 font-medium">
                Location
              </div>
              <Tooltip
                label={`${candidateData?.basic_details?.address || ""}, ${candidateData?.basic_details?.city?.name || ""}`}
                placement="top"
              >
                <div fontSize="sm" fontWeight="medium" className="text-sm font-medium text-gray-700">
                  {candidateData?.basic_details?.city?.name || "N/A"}, {candidateData?.basic_details?.state?.name || ""}
                </div>
              </Tooltip>
            </Box>
          </HStack>
        </VStack>

        <Divider my={4} />

        {/* Personal Details */}
        <Box>
          <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3} className="flex items-center">
            <IdentificationIcon className="h-4 w-4 mr-1.5 text-blue-600" />
            Personal Details
          </Text>

          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            <GridItem>
              <HStack spacing={2} align="flex-start">
                <Box bg="blue.100" p={1.5} borderRadius="md" display="flex" alignItems="center" justifyContent="center">
                  <IdentificationIcon className="h-3.5 w-3.5 text-blue.600" />
                </Box>
                <Box>
                  <div fontSize="xs" color="gray.500" fontWeight="medium" className="text-xs text-gray-500 font-medium">
                    Gender
                  </div>
                  <div fontSize="sm" className="text-sm font-medium text-gray-700">{candidateData?.basic_details?.gender || "N/A"}</div>
                </Box>
              </HStack>
            </GridItem>

            <GridItem>
              <HStack spacing={2} align="flex-start">
                <Box bg="blue.100" p={1.5} borderRadius="md" display="flex" alignItems="center" justifyContent="center">
                  <CakeIcon className="h-3.5 w-3.5 text-blue.600" />
                </Box>
                <Box>
                  <div fontSize="xs" color="gray.500" fontWeight="medium" className="text-xs text-gray-500 font-medium">
                    DOB
                  </div>
                  <div fontSize="sm" className="text-sm font-medium text-gray-700">
                    {candidateData?.basic_details?.dob
                      ? new Date(candidateData.basic_details.dob).toLocaleDateString()
                      : "N/A"}
                  </div>
                </Box>
              </HStack>
            </GridItem>

            <GridItem>
              <HStack spacing={2} align="flex-start">
                <Box bg="blue.100" p={1.5} borderRadius="md" display="flex" alignItems="center" justifyContent="center">
                  <HeartIcon className="h-3.5 w-3.5 text-blue.600" />
                </Box>
                <Box>
                  <div fontSize="xs" color="gray.500" fontWeight="medium" className="text-xs text-gray-500 font-medium">
                    Marital Status
                  </div>
                  <div fontSize="sm" className="text-sm font-medium text-gray-700">{candidateData?.basic_details?.marital_status || "N/A"}</div>
                </Box>
              </HStack>
            </GridItem>

            <GridItem>
              <HStack spacing={2} align="flex-start">
                <Box bg="blue.100" p={1.5} borderRadius="md" display="flex" alignItems="center" justifyContent="center">
                  <GlobeAltIcon className="h-3.5 w-3.5 text-blue.600" />
                </Box>
                <Box>
                  <div fontSize="xs" color="gray.500" fontWeight="medium" className="text-xs text-gray-500 font-medium">
                    Country
                  </div>
                  <div fontSize="sm" className="text-sm font-medium text-gray-700">{candidateData?.basic_details?.country?.name || "N/A"}</div>
                </Box>
              </HStack>
            </GridItem>
          </Grid>
        </Box>

        {/* Social Links */}
        {/* <Box mt={4}>
          <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3} className="flex items-center">
            <LinkIcon className="h-4 w-4 mr-1.5 text-blue-600" />
            Social Profiles
          </Text>

          <Flex justify="center" gap={3}>
            <Tooltip label="LinkedIn">
              <IconButton
                aria-label="LinkedIn"
                icon={
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                }
                variant="outline"
                colorScheme="blue"
                size="sm"
                borderRadius="full"
                className="hover:shadow-md transition-all"
              />
            </Tooltip>

            <Tooltip label="GitHub">
              <IconButton
                aria-label="GitHub"
                icon={
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
                  </svg>
                }
                variant="outline"
                colorScheme="gray"
                size="sm"
                borderRadius="full"
                className="hover:shadow-md transition-all"
              />
            </Tooltip>

            <Tooltip label="Portfolio">
              <IconButton
                aria-label="Portfolio"
                icon={<LinkIcon className="h-5 w-5" />}
                variant="outline"
                colorScheme="cyan"
                size="sm"
                borderRadius="full"
                className="hover:shadow-md transition-all"
              />
            </Tooltip>
          </Flex>
        </Box> */}

        {/* Resume Download Button */}
        {candidateData?.basic_details?.resume && (
          <Button
            leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
            colorScheme="blue"
            variant="outline"
            size="sm"
            mt={4}
            w="full"
            className="hover:shadow-md transition-all"
            onClick={() => openPdf(`${RESUME_VIEW}${candidateData?.basic_details?.resume}`)}
          >
            Download Resume
          </Button>
        )}
      </Box>
    </Card>
  )

  // Job Preferences Card Component
  const JobPreferencesCard = () => (
    <Card className="shadow-xl rounded-xl border border-gray-200 overflow-hidden transition-all duration-300 ">
      <Box p={5}>
        <Flex align="start" mb={4}>
          <Box bg="blue.50" p={2} borderRadius="lg" mr={2} className="shadow-sm">
            <BriefcaseIcon className="h-5 w-5 text-blue-600" />
          </Box>
          <Text fontSize="md" fontWeight="semibold">
            Job Preferences
          </Text>
        </Flex>

        <VStack align="stretch" spacing={4}>
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <Box p={3} bg="blue.50" borderRadius="lg" className="transition-all hover:shadow-md">
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                Expected Salary
              </Text>
              <HStack mt={1} alignItems={"center"}>
                <CurrencyRupeeIcon className="h-4 w-4 text-blue-600" />
                <div fontSize="sm" fontWeight="semibold" color="blue.700" className="text-sm font-semibold text-blue-900">
                  {candidateData?.job_preference?.[0]?.expected_salary_currency}{" "}
                  {formatSalary(candidateData?.job_preference?.[0]?.expected_salary_amount)}
                </div>
              </HStack>
            </Box>

            <Box p={3} bg="blue.50" borderRadius="lg" className="transition-all hover:shadow-md">
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                Current CTC
              </Text>
              <HStack mt={1} alignItems={"center"}>
                <CurrencyRupeeIcon className="h-4 w-4 text-blue-600" />
                <div fontSize="sm" fontWeight="semibold" color="blue.700" className="text-sm font-semibold text-blue-900">
                  {formatSalary(candidateData?.current_company_details?.current_salary_amount)}
                </div>
              </HStack>
            </Box>
          </Grid>

          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <Box p={3} bg="blue.50" borderRadius="lg" className="transition-all hover:shadow-md">
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                Notice Period
              </Text>
              <HStack mt={1} alignItems={"center"}>
                <ClockIcon className="h-4 w-4 text-blue-600" />
                <div fontSize="sm" fontWeight="semibold" color="blue.700" className="text-sm font-semibold text-blue-900">
                  {candidateData?.current_company_details?.notice_period || "N/A"}
                </div>
              </HStack>
            </Box>

            <Box p={3} bg="blue.50" borderRadius="lg" className="transition-all hover:shadow-md">
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                Job Type
              </Text>
              <HStack mt={1} alignItems={"center"}>
                <BriefcaseIcon className="h-4 w-4 text-blue-600" />
                <div fontSize="sm" fontWeight="semibold" color="blue.700" className="text-sm font-semibold text-blue-900">
                  {candidateData?.job_preference?.[0]?.job_type || "N/A"}
                </div>
              </HStack>
            </Box>
          </Grid>

          <Box p={3} bg="blue.50" borderRadius="lg" className="transition-all hover:shadow-md">
            <Text fontSize="xs" color="gray.500" fontWeight="medium">
              Preferred Locations
            </Text>
            <Flex mt={2} flexWrap="wrap" gap={2}>
              {candidateData?.job_preference?.[0]?.preference_location?.map((loc, index) => (
                <Tag key={index} size="sm" borderRadius="full" colorScheme="blue" variant="solid" className="shadow-sm">
                  <TagLabel>{loc.location?.title}</TagLabel>
                </Tag>
              )) || <Text fontSize="sm">No preferred locations specified</Text>}
            </Flex>
          </Box>

          <Box p={3} bg="blue.50" borderRadius="lg" className="transition-all hover:shadow-md">
            <Text fontSize="xs" color="gray.500" fontWeight="medium">
              Department & Shift
            </Text>
            <Grid templateColumns="repeat(2, 1fr)" gap={2} mt={1}>
              <HStack alignItems={"start"}>
                <BuildingOfficeIcon className="h-4 w-4 text-blue-600" />
                <div fontSize="sm" fontWeight="semibold" color="blue.700" className="text-sm font-semibold text-blue-900">
                  {candidateData?.job_preference?.[0]?.department?.title || "N/A"}
                </div>
              </HStack>

              <HStack alignItems={"start"}>
                <ClockIcon className="h-4 w-4 text-blue-600" />
                <div fontSize="sm" fontWeight="semibold" color="blue.700" className="text-sm font-semibold text-blue-900">
                  {candidateData?.job_preference?.[0]?.preferred_shift || "N/A"}
                </div>
              </HStack>
            </Grid>
          </Box>
        </VStack>
      </Box>
    </Card>
  )

  return (
    <div className="  ">
      <div className="bg-mainBg p-3">
        <div className="px-2 sm:px-4 sm:py-4 min-h-screen">

          <div >
            {/* Header with candidate name and mobile menu */}
            <Flex justify="space-between" align="center" mb={1} className="lg:hidden">
              <div className="mb-1">
                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                  <UserCircleIcon className="h-8 w-8 text-blue-600" />
                  Candidate Profile
                </h1>
                <p className="text-gray-500 mt-1">Detailed information about {candidateData?.name}</p>
              </div>
              <div className="block sm:hidden">

                <IconButton
                  aria-label="Open profile"
                  icon={<Bars3Icon className="h-5 w-5" />}
                  onClick={onOpen}
                  colorScheme="blue"
                  variant="ghost"
                  className="block sm:hidden"
                />
              </div>
            </Flex>

            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left Section - Profile (hidden on mobile) */}
              <div className="hidden lg:block lg:w-1/3">
                <ProfileCard />
                <JobPreferencesCard />
              </div>

              {/* Right Section - Skills, Projects, Education, Experience */}
              <div className="w-full lg:w-2/3">
                {/* Skills Card */}
                <Card className="shadow-xl rounded-xl border border-gray-200 overflow-hidden mb-6 transition-all duration-300 ">
                  <Box p={5}>
                    <Flex align="center" justify="space-between" mb={4}>
                      <HStack alignItems={"start"}>
                        <Box bg="blue.50" p={2} borderRadius="lg" className="shadow-sm">
                          <CodeBracketIcon className="h-5 w-5 text-blue-600" />
                        </Box>
                        <Text fontSize="md" fontWeight="semibold">
                          Skills & Expertise
                        </Text>
                      </HStack>
                      <Badge colorScheme="blue" fontSize="xs" px={2} py={1} borderRadius="full" className="shadow-sm">
                        {getAllSkills().length} Skills
                      </Badge>
                    </Flex>

                    <Flex flexWrap="wrap" gap={2} mb={5}>
                      {getAllSkills().map((skill, index) => (
                        <Tag
                          key={index}
                          size="md"
                          borderRadius="md"
                          colorScheme={index % 2 === 0 ? "blue" : "cyan"}
                          variant="subtle"
                          className="shadow-sm transition-all hover:shadow-md"
                        >
                          <TagLabel>{skill}</TagLabel>
                        </Tag>
                      ))}
                    </Flex>

                    {/* IT Skills with Experience */}
                    {candidateData?.it_skills && candidateData.it_skills.length > 0 && (
                      <Box mt={5}>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3} className="flex items-center">
                          <CodeBracketIcon className="h-4 w-4 mr-1.5 text-blue-600" />
                          IT Skills Proficiency
                        </Text>
                        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                          {candidateData.it_skills.map((skill, index) => {
                            const expLevel = getExperienceLevel(skill.experience_year)
                            return (
                              <GridItem
                                key={index}
                                bg="white"
                                p={4}
                                borderRadius="lg"
                                borderLeft="4px solid"
                                borderLeftColor={`${expLevel.color}.500`}
                                className="shadow-md transition-all hover:shadow-lg"
                              >
                                <Flex justify="space-between" align="center" mb={2}>
                                  <div fontSize="sm" fontWeight="semibold" className="font-semibold text-sm">
                                    {skill.skill?.title}
                                  </div>
                                  <Badge colorScheme={expLevel.color} fontSize="xs" className="shadow-sm">
                                    {expLevel.level}
                                  </Badge>
                                </Flex>
                                {skill.versions && (
                                  <Text fontSize="xs" color="gray.500" mb={1}>
                                    Version: {skill.versions}
                                  </Text>
                                )}
                                <Text fontSize="xs" color="gray.500" mb={2}>
                                  {skill.experience_year} years, {skill.experience_month} months
                                </Text>
                                <Progress
                                  value={expLevel.percentage}
                                  size="xs"
                                  colorScheme={expLevel.color}
                                  borderRadius="full"
                                  className="shadow-sm"
                                />
                              </GridItem>
                            )
                          })}
                        </Grid>
                      </Box>
                    )}
                  </Box>
                </Card>

                {/* Tabs Section */}
                <Card className="shadow-xl rounded-xl border border-gray-200 overflow-hidden transition-all duration-300 ">
                  <Tabs colorScheme="blue" variant="enclosed" p={0}>
                    <TabList px={4} pt={3} className="flex flex-wrap">
                      <Tab
                        _selected={{
                          color: "blue.600",
                          borderColor: "blue.600",
                          borderBottomColor: "white",
                          fontWeight: "semibold",
                        }}
                        className="flex items-center gap-1.5"
                      >
                        <BriefcaseIcon className="h-4 w-4" />
                        <span>Experience</span>
                      </Tab>
                      <Tab
                        _selected={{
                          color: "blue.600",
                          borderColor: "blue.600",
                          borderBottomColor: "white",
                          fontWeight: "semibold",
                        }}
                        className="flex items-center gap-1.5"
                      >
                        <DocumentTextIcon className="h-4 w-4" />
                        <span>Projects</span>
                      </Tab>
                      <Tab
                        _selected={{
                          color: "blue.600",
                          borderColor: "blue.600",
                          borderBottomColor: "white",
                          fontWeight: "semibold",
                        }}
                        className="flex items-center gap-1.5"
                      >
                        <AcademicCapIcon className="h-4 w-4" />
                        <span>Education</span>
                      </Tab>
                      <Tab
                        _selected={{
                          color: "blue.600",
                          borderColor: "blue.600",
                          borderBottomColor: "white",
                          fontWeight: "semibold",
                        }}
                        className="flex items-center gap-1.5"
                      >
                        <DocumentTextIcon className="h-4 w-4" />
                        <span>Certifications</span>
                      </Tab>
                    </TabList>

                    <TabPanels>
                      {/* Experience Tab */}
                      <TabPanel p={4}>
                        {candidateData?.employment && candidateData.employment.length > 0 ? (
                          <Stack spacing={4}>
                            {candidateData.employment.map((job, index) => (
                              <Box
                                key={index}
                                p={4}
                                borderWidth="1px"
                                borderRadius="lg"
                                bg="white"
                                className="shadow-md transition-all hover:shadow-lg"
                              >
                                <Flex justify="space-between" align="flex-start" mb={3}>
                                  <Box>
                                    <HStack mb={1}>
                                      <Box
                                        bg={job.is_current_employement ? "blue.100" : "gray.100"}
                                        p={2}
                                        borderRadius="md"
                                        className="shadow-sm"
                                      >
                                        <BuildingOfficeIcon
                                          className={`h-4 w-4 ${job.is_current_employement ? "text-blue-600" : "text-gray-600"}`}
                                        />
                                      </Box>
                                      <Text fontWeight="semibold">{job.current_company}</Text>
                                    </HStack>
                                    <Text color="blue.600" fontSize="sm" fontWeight="medium">
                                      {job.job_title?.title}
                                    </Text>
                                    <HStack mt={1} spacing={2}>
                                      <Badge colorScheme="purple" fontSize="xs">
                                        {job.employement_type}
                                      </Badge>
                                      <Badge colorScheme="teal" fontSize="xs">
                                        {job.department?.title}
                                      </Badge>
                                    </HStack>
                                  </Box>
                                  <Badge
                                    colorScheme={job.is_current_employement ? "blue" : "gray"}
                                    fontSize="xs"
                                    className="shadow-sm"
                                  >
                                    {job.is_current_employement ? "Current" : "Previous"}
                                  </Badge>
                                </Flex>

                                <HStack fontSize="xs" color="gray.500" mb={3}>
                                  <CalendarIcon className="h-3.5 w-3.5" />
                                  <div>
                                    {job.start_month}/{job.start_year} -{" "}
                                    {job.is_current_employement ? "Present" : `${job.end_month || '--'}/${job.end_year || '--'}`}
                                  </div>
                                  <div>•</div>
                                  <div>
                                    {job.experience_year} years, {job.experience_month} months
                                  </div>
                                </HStack>

                                <Divider my={2} />

                                <Grid templateColumns="repeat(2, 1fr)" gap={3} mt={3}>
                                  <Box>
                                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                                      Current Salary
                                    </Text>
                                    <Text fontSize="sm" color="gray.700">
                                      {job.current_salary_amount
                                        ? `${formatSalary(job.current_salary_amount)} ${job.current_salary_currency} (${job.current_pay_type})`
                                        : "Not specified"}
                                    </Text>
                                  </Box>

                                  <Box>
                                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                                      Notice Period
                                    </Text>
                                    <Text fontSize="sm" color="gray.700">
                                      {job.notice_period || "Not specified"}
                                    </Text>
                                  </Box>

                                  {!job.is_current_employement && (
                                    <Box>
                                      <Text fontSize="sm" fontWeight="medium" mb={1}>
                                        Last Working Date
                                      </Text>
                                      <Text fontSize="sm" color="gray.700">
                                        {job.last_working_date || "Not specified"}
                                      </Text>
                                    </Box>
                                  )}
                                </Grid>

                                <Box mt={4}>
                                  <Text fontSize="sm" fontWeight="medium" mb={1}>
                                    Job Profile
                                  </Text>
                                  <Text fontSize="sm" color="gray.700">
                                    {job.job_profile || "No job profile information available."}
                                  </Text>
                                </Box>

                                {job.has_any_offer ? (
                                  <Box mt={3} p={3} bg="blue.50" borderRadius="md" className="shadow-sm">
                                    <HStack color="blue.700" fontWeight="medium" fontSize="sm" mb={2}>
                                      <CheckBadgeIcon className="h-4 w-4" />
                                      <div>New Offer</div>
                                    </HStack>
                                    <Text fontSize="sm" color="blue.700">
                                      Offered position as{" "}
                                      <span className="font-semibold">{job.offered_job_title?.title || 'Not specified'}</span> at{" "}
                                      <span className="font-semibold">{job.offered_company || 'Not specified'}</span> with salary{" "}
                                      <span className="font-semibold">
                                        {job.offered_salary_amount
                                          ? `${formatSalary(job.offered_salary_amount)} ${job.offered_salary_currency} (${job.offered_pay_type})`
                                          : 'Not specified'}
                                      </span>
                                    </Text>
                                  </Box>
                                ) : (
                                  <Text mt={3} fontSize="sm" color="gray.600">
                                    No other offer
                                  </Text>
                                )}

                              </Box>
                            ))}
                          </Stack>
                        ) : (
                          <Flex direction="column" align="center" justify="center" py={8} bg="gray.50" borderRadius="md">
                            <BriefcaseIcon className="h-10 w-10 text-gray-400 mb-2" />
                            <Text color="gray.500">No employment history available</Text>
                          </Flex>
                        )}
                      </TabPanel>

                      {/* Projects Tab */}
                      <TabPanel p={4}>
                        {candidateData?.projects && candidateData.projects.length > 0 ? (
                          <Stack spacing={3}>
                            {candidateData.projects.map((project, index) => (
                              <Box
                                key={index}
                                p={3}
                                borderWidth="1px"
                                borderRadius="lg"
                                bg="white"
                                className="shadow-md transition-all hover:shadow-lg"
                              >
                                <Flex justify="space-between" align="flex-start" mb={2}>
                                  <Box>
                                    <Text fontWeight="semibold" color="blue.700" fontSize="md">
                                      {project.project_tag}
                                    </Text>
                                    <HStack mt={1} spacing={2}>
                                      <Badge
                                        colorScheme={project.project_status === "Completed" ? "green" : "blue"}
                                        fontSize="xs"
                                        className="shadow-sm"
                                      >
                                        {project.project_status}
                                      </Badge>
                                      <Badge colorScheme="purple" fontSize="xs">
                                        {project.employment_type}
                                      </Badge>
                                    </HStack>
                                  </Box>
                                  <Text fontSize="sm" color="gray.500">
                                    Team: {project.team_size || 'N/A'}
                                  </Text>
                                </Flex>

                                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={3} mb={2}>
                                  <Box>
                                    <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                      Duration
                                    </Text>
                                    <Text fontSize="sm">
                                      {project.work_from_month}/{project.work_from_year} -{' '}
                                      {project.work_till_month && project.work_till_year
                                        ? `${project.work_till_month}/${project.work_till_year}`
                                        : project.project_status === "Completed" ? "Present" : "Ongoing"}
                                    </Text>
                                  </Box>

                                  <Box>
                                    <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                      Role
                                    </Text>
                                    <Text fontSize="sm" fontWeight="medium">
                                      {project.role || 'N/A'}
                                    </Text>
                                  </Box>
                                </Grid>

                                <Box mb={2}>
                                  <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                    Skills Used
                                  </Text>
                                  <Flex flexWrap="wrap" gap={1} mt={1}>
                                    {project.skill_used ? (
                                      // Handle both formats - try JSON first, then fallback to string split
                                      (() => {
                                        try {
                                          // Try to parse as JSON first
                                          const skills = JSON.parse(project.skill_used.replace(/'/g, '"'));
                                          return skills.map((skill, i) => (
                                            <Badge key={i} colorScheme="blue" variant="subtle" fontSize="xs" className="shadow-sm">
                                              {skill}
                                            </Badge>
                                          ));
                                        } catch (e) {
                                          // If JSON parse fails, treat as comma-separated string
                                          return project.skill_used.split(',').map((skill, i) => (
                                            <Badge key={i} colorScheme="blue" variant="subtle" fontSize="xs" className="shadow-sm">
                                              {skill.trim()}
                                            </Badge>
                                          ));
                                        }
                                      })()
                                    ) : (
                                      <Text fontSize="sm">N/A</Text>
                                    )}
                                  </Flex>
                                </Box>

                                <Divider my={2} />

                                <Box mt={2} mb={2}>
                                  <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                    Role Description
                                  </Text>
                                  <Text fontSize="sm" mt={1}>
                                    {project.role_description || 'No description available'}
                                  </Text>
                                </Box>

                                <Box mt={2}>
                                  <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                    Project Description
                                  </Text>
                                  <Text fontSize="sm" mt={1}>
                                    {project.project_description || 'No description available'}
                                  </Text>
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        ) : (
                          <Flex direction="column" align="center" justify="center" py={8} bg="gray.50" borderRadius="md">
                            <DocumentTextIcon className="h-10 w-10 text-gray-400 mb-2" />
                            <Text color="gray.500">No projects available</Text>
                          </Flex>
                        )}
                      </TabPanel>

                      {/* Education Tab */}
                      <TabPanel p={4}>
                        {candidateData?.education && candidateData.education.length > 0 ? (
                          <Stack spacing={3}>
                            {candidateData.education.map((edu, index) => (
                              <Box
                                key={index}
                                p={3}
                                borderWidth="1px"
                                borderRadius="lg"
                                bg="white"
                                className="shadow-md transition-all hover:shadow-lg"
                              >
                                <Flex align="flex-start" gap={2}>
                                  <Box bg="blue.50" p={2} borderRadius="md" height="fit-content" className="shadow-sm">
                                    <AcademicCapIcon className="h-5 w-5 text-blue-600" />
                                  </Box>

                                  <Box flex="1">
                                    <Flex direction="column" gap={1}>
                                      <Text fontWeight="semibold" fontSize="md">{edu.education_course?.title}</Text>
                                      <div color="blue.600" fontSize="sm" fontWeight="medium" className="font-medium text-sm text-blue-900">
                                        {edu.insitute_university}
                                      </div>

                                      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={2} mt={1}>
                                        <Box>
                                          <Text fontSize="xs" color="gray.500" fontWeight="medium">Education Level</Text>
                                          <Text fontSize="sm">{edu.education?.title}</Text>
                                        </Box>
                                        <Box>
                                          <Text fontSize="xs" color="gray.500" fontWeight="medium">Specialization</Text>
                                          <Text fontSize="sm">{edu.education_course_specialty?.title || 'N/A'}</Text>
                                        </Box>
                                      </Grid>

                                      <HStack fontSize="xs" color="gray.500" mt={1}>
                                        <CalendarIcon className="h-3.5 w-3.5" />
                                        <div>
                                          {edu.course_duration_start_year} - {edu.course_duration_end_year}
                                        </div>
                                        <div>•</div>
                                        <div>{edu.course_type}</div>
                                      </HStack>

                                      <Flex mt={2} gap={2} flexWrap="wrap">
                                        {edu.marks && (
                                          <Badge colorScheme="blue" fontSize="xs" className="shadow-sm">
                                            {edu.grading_system}: {edu.marks}
                                          </Badge>
                                        )}

                                        {edu.primary_education && (
                                          <Badge colorScheme="green" fontSize="xs" className="shadow-sm">
                                            Primary Education
                                          </Badge>
                                        )}

                                        {edu.specialization && (
                                          <Badge colorScheme="purple" fontSize="xs" className="shadow-sm">
                                            Specialization: {edu.specialization}
                                          </Badge>
                                        )}
                                      </Flex>
                                    </Flex>
                                  </Box>
                                </Flex>
                              </Box>
                            ))}
                          </Stack>
                        ) : (
                          <Flex direction="column" align="center" justify="center" py={8} bg="gray.50" borderRadius="md">
                            <AcademicCapIcon className="h-10 w-10 text-gray-400 mb-2" />
                            <Text color="gray.500">No education history available</Text>
                          </Flex>
                        )}
                      </TabPanel>
                      <TabPanel p={4}>
                        {candidateData?.certifications && candidateData.certifications.length > 0 ? (
                          <Stack spacing={2}>
                            {candidateData.certifications.map((cert, index) => (
                              <Box
                                key={index}
                                p={3}
                                borderWidth="1px"
                                borderRadius="lg"
                                bg="white"
                                className="shadow-md transition-all hover:shadow-lg"
                              >
                                <Flex align="flex-start" gap={3}>
                                  <Box
                                    bg={cert.is_certification_expire ? "red.50" : "green.50"}
                                    p={2}
                                    borderRadius="md"
                                    flexShrink={0}
                                    className="shadow-sm"
                                  >
                                    <DocumentTextIcon className={`h-5 w-5 ${cert.is_certification_expire ? "text-red-600" : "text-green-600"}`} />
                                  </Box>

                                  <Box flex="1" minW={0}>
                                    <VStack align="stretch" spacing={2}>
                                      <Box>
                                        <Text fontWeight="semibold" fontSize="md" lineHeight="1.2">
                                          {cert.certification_name}
                                        </Text>
                                        <Text fontSize="sm" color="gray.600" mt={1}>
                                          Certification #: {cert.certification_number || 'N/A'}
                                        </Text>
                                      </Box>

                                      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={3}>
                                        <Box>
                                          <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1}>
                                            Issued On
                                          </Text>
                                          <Text fontSize="sm">
                                            {cert.certification_date ? new Date(cert.certification_date).toLocaleDateString() : 'N/A'}
                                          </Text>
                                        </Box>
                                        <Box>
                                          <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1}>
                                            Valid Until
                                          </Text>
                                          <Text fontSize="sm">
                                            {cert.certification_validity
                                              ? new Date(cert.certification_validity).toLocaleDateString()
                                              : 'No expiry'}
                                          </Text>
                                        </Box>
                                      </Grid>

                                      <HStack spacing={3} flexWrap="wrap">
                                        <Badge
                                          colorScheme={cert.is_certification_expire ? "red" : "green"}
                                          fontSize="xs"
                                          className="shadow-sm"
                                        >
                                          {cert.is_certification_expire ? "Expired" : "Active"}
                                        </Badge>

                                        {cert.certification_url && (
                                          <Button
                                            size="sm"
                                            colorScheme="blue"
                                            variant="link"
                                            leftIcon={<LinkIcon />}
                                            onClick={() => window.open(cert.certification_url, "_blank")}
                                          >
                                            View Certificate
                                          </Button>
                                        )}

                                      </HStack>
                                    </VStack>
                                  </Box>
                                </Flex>
                              </Box>
                            ))}
                          </Stack>
                        ) : (
                          <Flex direction="column" align="center" justify="center" py={8} bg="gray.50" borderRadius="md">
                            <DocumentTextIcon className="h-10 w-10 text-gray-400 mb-2" />
                            <Text color="gray.500">No certifications available</Text>
                          </Flex>
                        )}
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                </Card>
              </div>
            </div>
          </div>

          {/* Mobile Drawer */}
          <MobileProfileDrawer />
        </div>
      </div>
    </div>
  )
}

export default ShowCandidate
